
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Activity,
  Shield
} from 'lucide-react';

export const AlertDashboard = () => {
  // 静态大数据
  const alertStats = [
    { 
      title: '总告警数', 
      value: 127, 
      change: '+5.2%', 
      trend: 'up',
      icon: AlertTriangle,
      color: 'text-warning'
    },
    { 
      title: '紧急告警', 
      value: 8, 
      change: '+12.5%', 
      trend: 'up',
      icon: AlertCircle,
      color: 'text-destructive'
    },
    { 
      title: '已处理', 
      value: 95, 
      change: '+3.1%', 
      trend: 'up',
      icon: CheckCircle,
      color: 'text-success'
    },
    { 
      title: '系统状态', 
      value: '99.2%', 
      change: '+0.1%', 
      trend: 'up',
      icon: Shield,
      color: 'text-primary'
    }
  ];

  const recentAlerts = [
    {
      id: 1,
      level: 'critical',
      title: '主发动机 #1 温度异常',
      description: '当前温度 105°C，超过安全阈值',
      time: '2分钟前',
      location: '发动机舱 A区',
      source: 'TEMP_SENSOR_001'
    },
    {
      id: 2,
      level: 'warning',
      title: '燃油压力偏低',
      description: '燃油系统压力降至 1.8 Bar',
      time: '8分钟前',
      location: '燃油系统',
      source: 'FUEL_PRESS_002'
    },
    {
      id: 3,
      level: 'info',
      title: '定期维护提醒',
      description: '发动机 #2 计划维护时间临近',
      time: '15分钟前',
      location: '发动机舱 B区',
      source: 'MAINT_SCHEDULE'
    },
    {
      id: 4,
      level: 'critical',
      title: '冷却系统故障',
      description: '冷却液流量异常，可能存在泄漏',
      time: '23分钟前',
      location: '冷却系统',
      source: 'COOL_FLOW_003'
    },
    {
      id: 5,
      level: 'warning',
      title: '发电机负载过高',
      description: '发电机 #3 负载达到 92%',
      time: '35分钟前',
      location: '发电机房',
      source: 'GEN_LOAD_003'
    }
  ];

  const systemStatus = [
    { name: '发动机系统', status: 'normal', uptime: '99.8%', alerts: 2 },
    { name: '燃油系统', status: 'warning', uptime: '98.5%', alerts: 5 },
    { name: '电力系统', status: 'normal', uptime: '99.9%', alerts: 1 },
    { name: '冷却系统', status: 'critical', uptime: '95.2%', alerts: 8 },
    { name: '排放系统', status: 'normal', uptime: '99.1%', alerts: 0 },
    { name: '控制系统', status: 'normal', uptime: '100%', alerts: 0 }
  ];

  const getAlertIcon = (level: string) => {
    switch (level) {
      case 'critical': return <AlertTriangle className="h-4 w-4 text-destructive" />;
      case 'warning': return <AlertCircle className="h-4 w-4 text-warning" />;
      case 'info': return <Info className="h-4 w-4 text-primary" />;
      default: return <Info className="h-4 w-4" />;
    }
  };

  const getAlertBadge = (level: string) => {
    switch (level) {
      case 'critical': return 'status-danger';
      case 'warning': return 'status-warning';
      case 'info': return 'status-active';
      default: return 'tech-badge';
    }
  };

  const getSystemBadge = (status: string) => {
    switch (status) {
      case 'normal': return 'status-normal';
      case 'warning': return 'status-warning';
      case 'critical': return 'status-danger';
      default: return 'tech-badge';
    }
  };

  return (
    <div className="space-y-6">
      {/* 告警统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {alertStats.map((stat, index) => (
          <Card key={index} className="tech-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                  <div className="flex items-center mt-1">
                    {stat.trend === 'up' ? 
                      <TrendingUp className="h-3 w-3 text-success mr-1" /> : 
                      <TrendingDown className="h-3 w-3 text-destructive mr-1" />
                    }
                    <span className={`text-xs ${stat.trend === 'up' ? 'text-success' : 'text-destructive'}`}>
                      {stat.change}
                    </span>
                  </div>
                </div>
                <stat.icon className={`h-8 w-8 ${stat.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 最新告警列表 */}
        <Card className="tech-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium">最新告警</CardTitle>
              <Badge className="status-active">实时更新</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentAlerts.map((alert) => (
              <Alert key={alert.id} className="tech-card">
                <div className="flex items-start space-x-3">
                  {getAlertIcon(alert.level)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-foreground truncate">
                        {alert.title}
                      </h4>
                      <Badge className={getAlertBadge(alert.level)}>
                        {alert.level.toUpperCase()}
                      </Badge>
                    </div>
                    <AlertDescription className="text-xs text-muted-foreground mt-1">
                      {alert.description}
                    </AlertDescription>
                    <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                      <span>{alert.location}</span>
                      <span>{alert.time}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      传感器: {alert.source}
                    </div>
                  </div>
                </div>
              </Alert>
            ))}
          </CardContent>
        </Card>

        {/* 系统状态监控 */}
        <Card className="tech-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium">系统状态监控</CardTitle>
              <div className="flex items-center space-x-2">
                <Activity className="h-4 w-4 text-primary pulse-glow" />
                <span className="text-sm text-muted-foreground">在线监控</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {systemStatus.map((system, index) => (
              <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full device-indicator ${
                    system.status === 'normal' ? 'device-running' :
                    system.status === 'warning' ? 'device-warning' : 'device-stopped'
                  }`}></div>
                  <div>
                    <div className="text-sm font-medium text-foreground">{system.name}</div>
                    <div className="text-xs text-muted-foreground">运行时间: {system.uptime}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge className={getSystemBadge(system.status)}>
                    {system.status.toUpperCase()}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {system.alerts} 告警
                  </span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
